/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class ServiceabilityRenderingInfoImpl
 * @hideconstructor
 * @classdesc Render Service Implementation class
 */
define([
	'react',
	'@ba/ba-ui-carbon-toolkit',
	'../../../../api/impl/serviceability/nls/StringResources'
], (React, UI_TOOLKIT, StringResources) => {

	const {
		ToggleSwitchSection,
		FlexItem,
		FlexLayout,
		Link,
		Label,
		Container,
		Paragraph
	} = UI_TOOLKIT;

	const {
		useState,
		useEffect
	} = React;

	class ServiceabilityRenderingInfo {
		constructor(content, dashboard) {
			this._dashboard = dashboard;
			this._content = content;
			this._logger = dashboard.getFeature('Logger');
			this._s12yFeature = dashboard.getFeature('Serviceability');
			this._performanceFeature = content.getFeature('Performance');
			this._renderingInfo = undefined;
			this._zipiIds = [];
		}

		_isIPAActive() {
			return this._performanceFeature ? this._performanceFeature.isIPAActive() : false;
		}

		/**
		 * @returns {RenderingInfo} the current rendering info object
		 */
		getCurrentRendering() {
			return this._renderingInfo;
		}

		/**
		 * @param {RenderingInfo} renderingInfo the info of a rendersequence which is being executed
		 */
		setCurrentRendering(renderingInfo) {
			this._renderingInfo = renderingInfo;
		}

		/**
		 * @returns {RenderingInfo} A RenderingInfo object represent the latest valid rendering info
		 */
		getLastRendering() {
			return this._lastRendering;
		}

		/**
		 * @description set the latest valid rendering info object
		 * @param {RenderingInfo} lastRendering An object of class RenderingInfo
		 */
		setLastRendering(lastRendering) {
			this._lastRendering = lastRendering;
		}

		setZipiIds(zipiIds) {
			this._zipiIds = zipiIds;
		}

		getZipiIds() {
			return this._zipiIds;
		}

		_buildZipiUrl(id) {
			const urlObj = {
				pathRef: encodeURIComponent('.public_folders/ZIPI/Interactive+Performance+Assistant/ProcessTimeDashboard'),
				format: 'HTML',
				Download: 'false',
				prompt: 'false',
				action: 'run',
				promptParameters: encodeURIComponent(JSON.stringify({
					name: 'zipiid',
					value: [
						{
							display: id,
							use: id
						}
					]
				}))
			};
			const url = '/bi/?' + Object
				.keys(urlObj)
				.map(key => {
					const value = urlObj[key];
					return `${key}=${value}`;
				})
				.join('&');
			return url;
		}

		_getTargetContents() {
			const allSelectedPages = this._dashboard.getCanvas().getSelectedContentList({ type: 'page' });
			const selectedPage = allSelectedPages.length > 0 ? allSelectedPages[0] : null;
			if (selectedPage) {
				return selectedPage.findContent({ type: 'widget.live' });
			} else {
				return [];
			}
		}

		_activateIPA() {
			this.setZipiIds([]);
			const contents = this._getTargetContents();
			const promises = contents.map(content => {
				const performance = content.getFeature('Performance');
				if (performance) {
					if (!performance.isIPAActive()) {
						performance.activateIPA();
					}
					return content.getFeature('WidgetAPI.deprecated')
						.getVisApi().reRender({});
				}
			});
			return Promise.all(promises);
		}

		_deactivateIPA() {
			const contents = this._getTargetContents();
			contents.forEach(content => {
				const performanceFeature = content.getFeature('Performance');
				if (performanceFeature) {
					performanceFeature.deactivateIPA();
				}
			});
		}

		toJSON() {
			return {
				renderingInfo: {
					current: (this._renderingInfo ? this._renderingInfo.toJSON() : {}),
					last: (this._lastRendering ? this._lastRendering.toJSON() : {})
				}
			};
		}

		/**
		 * @description Returns a json which currently will be consumed by Serviceability feature
		 * @returns a json represent the desired UI
		 */
		toUIJSON({ mode } = {}) {

			if (mode === 'overlay') {
				const requestDurations = this._getRequestDurations();
				return [
					{
						sectionName: StringResources.get('performanceSection'),
						sectionValues: [
							{
								fieldName: StringResources.get('performanceDetails'),
								node: (
									<this.RequestDuration
										requestDurations={requestDurations}
										mode={mode}
									/>
								),
								type: 'ReactNode'
							}
						]
					}
				];
			}

			return [
				{
					sectionName: StringResources.get('performanceSection'),
					sectionValues: [
						{
							fieldName: StringResources.get('performanceDetails'),
							node: (
								<this.PerformanceDetails
									initialShowPerformance={this._isIPAActive()}
									initialShowInfo={this._s12yFeature.isInfoVisible()}
									onToggle={(toggleState, setUrl) => {
										if (toggleState) {
											return this._activateIPA()
												.then(() => {
													setUrl(this._zipiIds);
												});
										} else {
											setUrl(undefined);
											this._deactivateIPA();
											this.setZipiIds([]);
										}
									}}
								/>
							),
							type: 'ReactNode'
						}
					]
				}
			];
		}

		_getRequestDurations() {
			let requestDurations = [], total_duration;
			if(this.getLastRendering()) {
				total_duration = this.getLastRendering().getDuration();
				let taskInfo = this.getLastRendering().getRenderSequenceWithData();
				requestDurations = taskInfo.getTaskInfo('data').getRequestDurations();
			} else if (this.getCurrentRendering()) {
				total_duration = this.getCurrentRendering().getDuration();
				let taskInfo = this.getCurrentRendering().getRenderSequenceWithData();
				if (taskInfo === undefined) {
					total_duration = total_duration + ' ' + StringResources.get('renderInProgress');
				} else {
					requestDurations = taskInfo.getTaskInfo('data').getRequestDurations();
					total_duration = total_duration + ' ' + StringResources.get('renderInProgress');
				}
			}

			const cellData = [
				{
					id: 'serviceability_field_total(ms)',
					label: StringResources.get('liveWidgetTotalTime'),
					duration: total_duration
				}
			];


			const serverFieldId = 'serviceability_field_serverquery(ms)';
			if (requestDurations.length === 0) {
				cellData.push({
					id: serverFieldId,
					label: StringResources.get('singleQueryTime'),
					duration: StringResources.get('infoNotAvailable')
				});
			} else {
				const zipiIds = this.getZipiIds();
				requestDurations.forEach((duration, index) => {
					let label = '';
					let id;
					if (requestDurations.length === 1) {
						label = StringResources.get('singleQueryTime');
						id = serverFieldId;
					} else {
						label = StringResources.get('multipleQueryTime', { name: index + 1 });
						id = serverFieldId + (index + 1);
					}
					if (isNaN(duration)) {
						cellData.push({
							id,
							label,
							duration: StringResources.get('infoNotAvailable')
						});
					} else {
						const cellItem = {
							id,
							label,
							duration
						};
						const zipi = zipiIds[index];
						if (this._isIPAActive() && zipi) {
							cellItem.zipiUrl = this._buildZipiUrl(zipi);
						}
						cellData.push(cellItem);
					}
				});
			}
			return cellData;
		}

		RequestDuration(props) {
			const { requestDurations, mode } = props;
			return (
				<React.Fragment>
					{mode !== 'overlay' &&
						<FlexItem>
							<Label className="labelText" label={StringResources.get('durationField')}/>
						</FlexItem>
					}
					{requestDurations.map((item, key) => {
						const { id, label, duration, zipiUrl } = item;
						let durationElm;
						if (zipiUrl) {
							durationElm = (
								<Link
									target="_blank"
									href={zipiUrl}
								>
									{duration}
								</Link>
							);
						} else {
							durationElm = duration;
						}

						return (
							<Container key={key} id={id} className={'s12yField'}>
								<Label className="s12yFieldTitle" label={label} type="caption" />
								<Paragraph className="s12yFieldValue">{durationElm}</Paragraph>
							</Container>
						);

					})}
				</React.Fragment>
			);
		}

		PerformanceDetails = (props) => {
			const {
				onToggle,
				initialShowPerformance,
				initialShowInfo
			} = props;

			const [showPerformance, setShowPerformance] = useState(initialShowPerformance);
			const urlState = useState();
			const setUrl = urlState[1];
			const [showInfo, setShowInfo] = useState(initialShowInfo);

			const requestDurations = this._getRequestDurations();

			useEffect(() => {
				onToggle(showPerformance, setUrl);
			}, [showPerformance]);

			return (
				<FlexLayout direction={'column'}>
					<this.RequestDuration
						requestDurations={requestDurations}
					/>
					<FlexItem>
						<ToggleSwitchSection
							label={StringResources.get('showInfoForAll')}
							rtl={false}
							className="showToggleSwitch"
							checked={showInfo}
							small={true}
							onChange={(value) => {
								if(value) {
									this._s12yFeature.showInfo();
								} else {
									this._s12yFeature.hideInfo();
								}
								setShowInfo(value);
							}}
						/>
					</FlexItem>
					{this._performanceFeature ? <FlexItem>
						<ToggleSwitchSection
							label={StringResources.get('showPerformanceDetails')}
							rtl={false}
							className={'performanceDetailsToggle'}
							checked={showPerformance}
							small={true}
							onChange={() => setShowPerformance(!showPerformance)}
						/>
					</FlexItem>: null}
				</FlexLayout>
			);

		}
	}
	return ServiceabilityRenderingInfo;
});
